
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import { alpha, styled } from '@mui/material/styles';

/*
 * How loud a selected row is, per mode.
 *
 * This is added to MUI's own selected and hover opacities rather than setting them, so it
 * decides the result rather than being it: 0.2 lands a selected row at 0.36 of the accent and
 * a hovered selected row at 0.44. Against a white page with the muted primary the console had,
 * that is the highlight it always was. Against a dark page with a brighter accent it is a block
 * of saturated blue, so dark uses 0.10 and lands them at 0.26 and 0.34.
 */
export const SELECTION_STRENGTH = { dark: 0.10, light: 0.2 };

/*
 * The banding on alternate rows, per mode.
 *
 * Light is the pale green this console has always used. Dark is a tint of the page's own text,
 * because that green at a tenth of an opacity is invisible against a near-black surface - which
 * is the whole reason there are two values rather than one.
 */
export const ROW_BANDING = {
    light: (theme) => alpha('#ACD675', 0.1),
    dark: (theme) => alpha(theme.palette.text.primary, 0.04)
};

export const CustomDataGrid = styled(DataGrid)(({ theme }) => {
    const dark = theme.palette.mode === 'dark';
    const ODD_OPACITY = SELECTION_STRENGTH[theme.palette.mode] ?? 0.2;
    const banding = (ROW_BANDING[theme.palette.mode] ?? ROW_BANDING.light)(theme);

    return {
    /*
     * Column headings, told apart from the data under them. They resolved to the same colour as
     * the cells, so a grid read as one even field of text with its headings marked only by the
     * rule beneath them - and on a screen that is mostly grid, that is most of the screen.
     *
     * The header row also sits on the page colour rather than the card's, which puts the same
     * frame-and-content ordering inside the grid that the shell has around it.
     *
     * Dark only, with the banding above. This is the dark theme's hierarchy work, and the light
     * theme is meant to be the grid this console already had rather than a lighter version of
     * this one - so on light, MUI's own header and footer are left alone.
     */
    ...(dark ? {
        [`& .${gridClasses.columnHeaders}`]: {
            backgroundColor: theme.palette.background.default
        },

        [`& .${gridClasses.columnHeaderTitle}`]: {
            color: theme.palette.text.secondary,
            fontWeight: 600,
            letterSpacing: '0.03em'
        },

        // The footer carries counts and paging controls, which are chrome rather than data.
        [`& .${gridClasses.footerContainer}`]: {
            backgroundColor: theme.palette.background.default
        }
    } : {}),

    [`& .${gridClasses.row}.even`]: {
        backgroundColor: banding,
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        },
        '&.Mui-selected': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                ODD_OPACITY + theme.palette.action.selectedOpacity,
            ),
            '&:hover, &.Mui-hovered': {
                backgroundColor: alpha(
                    theme.palette.primary.main,
                    ODD_OPACITY +
                    theme.palette.action.selectedOpacity +
                    theme.palette.action.hoverOpacity,
                ),
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(
                        theme.palette.primary.main,
                        ODD_OPACITY + theme.palette.action.selectedOpacity,
                    ),
                },
            },
        },
    },

    [`& .${gridClasses.row}.inactive`]: {
        //backgroundColor: theme.palette.grey[200],
        // Dimmed relative to the text colour rather than towards black, which on this page
        // made an inactive row unreadable instead of quiet.
        color: theme.palette.text.secondary,
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        }
    },

    [`& .${gridClasses.row}.notification`]: {
         backgroundColor: alpha(theme.palette.primary.main, 0.2),
        '&:hover, &.Mui-hovered': {
            backgroundColor: alpha(theme.palette.primary.main, 0.5),
            '@media (hover: none)': {
                backgroundColor: 'transparent',
            },
        }
    },   
    };
});
